import { useState, useEffect } from 'react';
import { PathNode } from './jsonPathUtils';

export interface SearchResult {
  node: PathNode;
  matchType: 'path' | 'value' | 'both';
  matchedText: string;
}

export function searchPaths(
  nodes: PathNode[], 
  query: string, 
  isRegex: boolean = false
): SearchResult[] {
  if (!query.trim()) return [];

  const results: SearchResult[] = [];
  
  const searchPattern = isRegex 
    ? (() => {
        try {
          return new RegExp(query, 'i');
        } catch {
          return null;
        }
      })()
    : null;

  function searchNode(node: PathNode) {
    const pathMatches = isRegex 
      ? searchPattern?.test(node.path || '') 
      : (node.path || '').toLowerCase().includes(query.toLowerCase());

    const valueString = getValueString(node.value);
    const valueMatches = isRegex
      ? searchPattern?.test(valueString)
      : valueString.toLowerCase().includes(query.toLowerCase());

    if (pathMatches || valueMatches) {
      let matchType: 'path' | 'value' | 'both';
      let matchedText: string;

      if (pathMatches && valueMatches) {
        matchType = 'both';
        matchedText = `${node.path}: ${valueString}`;
      } else if (pathMatches) {
        matchType = 'path';
        matchedText = node.path || '';
      } else {
        matchType = 'value';
        matchedText = valueString;
      }

      results.push({
        node,
        matchType,
        matchedText
      });
    }

    if (node.children) {
      node.children.forEach(searchNode);
    }
  }

  nodes.forEach(searchNode);
  return results;
}

export function highlightMatches(
  text: string, 
  query: string, 
  isRegex: boolean = false
): string {
  if (!query.trim() || !text) return text;

  try {
    const pattern = isRegex 
      ? new RegExp(`(${query})`, 'gi')
      : new RegExp(`(${escapeRegExp(query)})`, 'gi');

    return text.replace(pattern, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
  } catch {
    return text;
  }
}

export function getMatchingNodePaths(
  nodes: PathNode[], 
  query: string, 
  isRegex: boolean = false
): Set<string> {
  const searchResults = searchPaths(nodes, query, isRegex);
  const matchingPaths = new Set<string>();

  searchResults.forEach(result => {
    matchingPaths.add(result.node.path);
    
    // Also add parent paths to ensure they're expanded
    let currentPath = result.node.path;
    while (currentPath && currentPath.includes('.')) {
      const lastDotIndex = currentPath.lastIndexOf('.');
      currentPath = currentPath.substring(0, lastDotIndex);
      matchingPaths.add(currentPath);
    }
    
    // Handle array indices
    if (result.node.path.includes('[')) {
      const bracketIndex = result.node.path.lastIndexOf('[');
      if (bracketIndex > 0) {
        const parentPath = result.node.path.substring(0, bracketIndex);
        matchingPaths.add(parentPath);
      }
    }
  });

  return matchingPaths;
}

function getValueString(value: any): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (typeof value === 'object') return `Object(${Object.keys(value).length})`;
  return String(value);
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Custom hook for debouncing values

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}